import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import {
  MonitoredTopicTemplate,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  ApplyTemplateRequest,
  ApplyTemplateResponse,
  ListTemplatesResponse,
  GetTemplateResponse,
  CreateTemplateResponse,
  SaveDeviceConfigAsTemplateRequest,
} from "../../interfaces/Templates";
import { ArboristNodeData } from "../../lib/utils";

// --- Define State Interface ---
interface TemplateState {
  templates: MonitoredTopicTemplate[];
  defaultTemplate: MonitoredTopicTemplate | null;
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
  discoveredSources: ArboristNodeData[]; // Sources available for template editing
  discoveredSourcesFetched: boolean; // prevent repeated fetching
}

// --- Initial State ---
const initialState: TemplateState = {
  templates: [],
  defaultTemplate: null,
  loading: "idle",
  error: null,
  discoveredSources: [],
  discoveredSourcesFetched: false,
};

// --- Async Thunks ---

// Fetch all templates
export const fetchTemplates = createAsyncThunk<
  MonitoredTopicTemplate[],
  void,
  { rejectValue: string }
>("templates/fetchTemplates", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get<ListTemplatesResponse>("/templates");
    return response.data.templates;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.error ||
      error.message ||
      "Failed to fetch templates";
    console.error("Error fetching templates:", errorMsg);
    return rejectWithValue(errorMsg);
  }
});

// Fetch default template
export const fetchDefaultTemplate = createAsyncThunk<
  MonitoredTopicTemplate | null,
  void,
  { rejectValue: string }
>("templates/fetchDefaultTemplate", async (_, { rejectWithValue }) => {
  try {
    const response =
      await apiClient.get<GetTemplateResponse>("/templates/default");
    return response.data.template;
  } catch (error: any) {
    // 404 is expected if no default template exists
    if (error.response?.status === 404) {
      return null;
    }
    const errorMsg =
      error.response?.data?.error ||
      error.message ||
      "Failed to fetch default template";
    console.error("Error fetching default template:", errorMsg);
    return rejectWithValue(errorMsg);
  }
});

// Fetch single template with topics
export const fetchTemplate = createAsyncThunk<
  MonitoredTopicTemplate,
  { templateId: string },
  { rejectValue: string }
>("templates/fetchTemplate", async ({ templateId }, { rejectWithValue }) => {
  try {
    const response = await apiClient.get<GetTemplateResponse>(
      `/templates/${templateId}`,
    );
    return response.data.template;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.error ||
      error.message ||
      "Failed to fetch template";
    console.error("Error fetching template:", errorMsg);
    return rejectWithValue(errorMsg);
  }
});

// Create new template
export const createTemplate = createAsyncThunk<
  string, // Return template ID
  CreateTemplateRequest,
  { rejectValue: string }
>("templates/createTemplate", async (req, { rejectWithValue }) => {
  try {
    const response = await apiClient.post<CreateTemplateResponse>(
      "/templates",
      req,
    );
    toast.success("Template Created", {
      description: `Template "${req.name}" has been created successfully.`,
    });
    return response.data.id;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.error ||
      error.message ||
      "Failed to create template";
    console.error("Error creating template:", errorMsg);
    toast.error("Failed to Create Template", {
      description: errorMsg,
    });
    return rejectWithValue(errorMsg);
  }
});

// Update template
export const updateTemplate = createAsyncThunk<
  void,
  { templateId: string; updates: UpdateTemplateRequest },
  { rejectValue: string }
>(
  "templates/updateTemplate",
  async ({ templateId, updates }, { rejectWithValue }) => {
    try {
      await apiClient.patch(`/templates/${templateId}`, updates);
      toast.success("Template Updated", {
        description: "Template has been updated successfully.",
      });
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Failed to update template";
      console.error("Error updating template:", errorMsg);
      toast.error("Failed to Update Template", {
        description: errorMsg,
      });
      return rejectWithValue(errorMsg);
    }
  },
);

// Delete template
export const deleteTemplate = createAsyncThunk<
  string, // Return deleted template ID
  { templateId: string },
  { rejectValue: string }
>("templates/deleteTemplate", async ({ templateId }, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/templates/${templateId}`);
    toast.success("Template Deleted", {
      description: "Template has been deleted successfully.",
    });
    return templateId;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.error ||
      error.message ||
      "Failed to delete template";
    console.error("Error deleting template:", errorMsg);
    toast.error("Failed to Delete Template", {
      description: errorMsg,
    });
    return rejectWithValue(errorMsg);
  }
});

// Apply template to devices
export const applyTemplate = createAsyncThunk<
  ApplyTemplateResponse,
  { templateId: string; request: ApplyTemplateRequest },
  { rejectValue: string }
>(
  "templates/applyTemplate",
  async ({ templateId, request }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<ApplyTemplateResponse>(
        `/templates/${templateId}/apply`,
        request,
      );

      const { successful_devices, failed_devices } = response.data;

      if (successful_devices.length > 0) {
        toast.success("Template Applied", {
          description: `Template applied successfully to ${successful_devices.length} device(s).`,
        });
      }

      if (failed_devices && failed_devices.length > 0) {
        toast.warning("Partial Success", {
          description: `Template applied to ${successful_devices.length} devices, but failed for ${failed_devices.length} device(s).`,
        });
      }

      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Failed to apply template";
      console.error("Error applying template:", errorMsg);
      toast.error("Failed to Apply Template", {
        description: errorMsg,
      });
      return rejectWithValue(errorMsg);
    }
  },
);

// Save device config as template
export const saveDeviceConfigAsTemplate = createAsyncThunk<
  string, // Return template ID
  { deviceId: string; request: SaveDeviceConfigAsTemplateRequest },
  { rejectValue: string }
>(
  "templates/saveDeviceConfigAsTemplate",
  async ({ deviceId, request }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<CreateTemplateResponse>(
        `/devices/${deviceId}/save-as-template`,
        request,
      );
      toast.success("Template Saved", {
        description: `Device configuration saved as template "${request.name}".`,
      });
      return response.data.id;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Failed to save device config as template";
      console.error("Error saving device config as template:", errorMsg);
      toast.error("Failed to Save Template", {
        description: errorMsg,
      });
      return rejectWithValue(errorMsg);
    }
  },
);

// Get discovered sources for template editing
export const fetchDiscoveredSourcesForTemplating = createAsyncThunk<
  any[], // Array of discovered sources
  void,
  { rejectValue: string }
>(
  "templates/fetchDiscoveredSourcesForTemplating",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<{ sources: any[] }>(
        "/templates/discovered-sources",
      );
      return response.data.sources;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch discovered sources";
      console.error(
        "Error fetching discovered sources for templating:",
        errorMsg,
      );
      return rejectWithValue(errorMsg);
    }
  },
);

// --- Slice Definition ---
const templateSlice = createSlice({
  name: "templates",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Optimistic updates for UI responsiveness
    setTemplateAsDefault: (state, action: PayloadAction<string>) => {
      const templateId = action.payload;
      // Unset current default
      state.templates.forEach((template) => {
        template.is_default = false;
      });
      // Set new default
      const template = state.templates.find((t) => t.id === templateId);
      if (template) {
        template.is_default = true;
        state.defaultTemplate = template;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Templates
      .addCase(fetchTemplates.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.templates = action.payload;
        // Update default template reference
        state.defaultTemplate =
          action.payload.find((t) => t.is_default) || null;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload ?? "Failed to fetch templates";
      })

      // Fetch Default Template
      .addCase(fetchDefaultTemplate.fulfilled, (state, action) => {
        state.defaultTemplate = action.payload;
      })
      .addCase(fetchDefaultTemplate.rejected, (state) => {
        // Don't set error for default template fetch failures
        state.defaultTemplate = null;
      })

      // Fetch Single Template
      .addCase(fetchTemplate.fulfilled, (state, action) => {
        const updatedTemplate = action.payload;
        const index = state.templates.findIndex(
          (t) => t.id === updatedTemplate.id,
        );
        if (index !== -1) {
          state.templates[index] = updatedTemplate;
        } else {
          state.templates.push(updatedTemplate);
        }

        if (updatedTemplate.is_default) {
          state.defaultTemplate = updatedTemplate;
        }
      })

      // Create Template
      .addCase(createTemplate.fulfilled, () => {
        // Template will be fetched again or added to list when templates are refreshed
        // We could optimistically add it here if we had the full template object
      })

      // Update Template
      .addCase(updateTemplate.fulfilled, () => {
        // Refresh templates list to get updated data
        // Alternatively, we could update the template in place if we had the updated data
      })

      // Delete Template
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.templates = state.templates.filter((t) => t.id !== deletedId);
        if (state.defaultTemplate?.id === deletedId) {
          state.defaultTemplate = null;
        }
      })

      // Apply Template
      .addCase(applyTemplate.fulfilled, () => {
        // Template application succeeded - no state changes needed
        // Devices will refresh their monitored topics separately
      })

      // Save Device Config as Template
      .addCase(saveDeviceConfigAsTemplate.fulfilled, () => {
        // Template was created - refresh templates list to include it
        // We could optimistically add it here if we had the full template object
      })

      // Fetch Discovered Sources for Templating
      .addCase(fetchDiscoveredSourcesForTemplating.pending, (state) => {
        // Only set loading if we have not fetched before; keep existing data to avoid flicker
        if (!state.discoveredSourcesFetched) {
          state.loading = "pending";
        }
        state.error = null;
      })
      .addCase(
        fetchDiscoveredSourcesForTemplating.fulfilled,
        (state, action) => {
          state.loading = "succeeded";
          state.discoveredSources = action.payload;
          state.discoveredSourcesFetched = true;
        },
      )
      .addCase(
        fetchDiscoveredSourcesForTemplating.rejected,
        (state, action) => {
          state.loading = "failed";
          state.error = action.payload ?? "Failed to fetch discovered sources";
        },
      )

      // Handle errors for all actions
      .addMatcher(
        (action) => action.type.endsWith("/rejected"),
        (state, action: any) => {
          if (action.type !== "templates/fetchDefaultTemplate/rejected") {
            state.error = action.payload ?? "An error occurred";
          }
        },
      );
  },
});

// --- Export Actions and Reducer ---
export const { clearError, setTemplateAsDefault } = templateSlice.actions;
export const templateReducer = templateSlice.reducer;

// --- Selectors ---
export const selectAllTemplates = (state: {
  templateReducer: TemplateState;
}): MonitoredTopicTemplate[] => state.templateReducer.templates;

export const selectDefaultTemplate = (state: {
  templateReducer: TemplateState;
}): MonitoredTopicTemplate | null => state.templateReducer.defaultTemplate;

export const selectTemplateLoadingStatus = (state: {
  templateReducer: TemplateState;
}): "idle" | "pending" | "succeeded" | "failed" =>
  state.templateReducer.loading;

export const selectTemplateError = (state: {
  templateReducer: TemplateState;
}): string | null => state.templateReducer.error;

export const selectDiscoveredSourcesForTemplating = (state: {
  templateReducer: TemplateState;
}): ArboristNodeData[] => state.templateReducer.discoveredSources;

export const selectDiscoveredSourcesFetched = (state: {
  templateReducer: TemplateState;
}): boolean => state.templateReducer.discoveredSourcesFetched;

export const selectTemplateById = (
  state: { templateReducer: TemplateState },
  templateId: string,
): MonitoredTopicTemplate | undefined =>
  state.templateReducer.templates.find(
    (template) => template.id === templateId,
  );

export const selectTemplatesByName = (state: {
  templateReducer: TemplateState;
}): Record<string, MonitoredTopicTemplate> =>
  state.templateReducer.templates.reduce(
    (acc, template) => {
      acc[template.name] = template;
      return acc;
    },
    {} as Record<string, MonitoredTopicTemplate>,
  );
